/**
 * Date and number display helpers.
 *
 * A note on timezones: the Analytics `from`/`to` parameters are calendar days
 * **on the viewer's own clock**, and the API resolves them against the `tz` this
 * app sends. So "today" means the day the person reading the screen is having,
 * which is what a date picker implies — and it is what makes the Analytics
 * window agree with the Credit Usage card's "Calls today".
 *
 * Day labels are still rendered verbatim from the key the server grouped by:
 * they are already days in the zone being reported, so converting them to local
 * time would make the label disagree with the grouping that produced it.
 * Timestamps (`createdAt` and friends) are instants and are shown in local time.
 */

function pad(n: number): string {
  return n.toString().padStart(2, '0')
}

/**
 * The IANA zone this browser is in, for the API's `tz` parameter.
 *
 * Every modern browser supplies this, but a locked-down or very old one may not,
 * and an empty value would make the server reject the request rather than guess.
 * UTC is the right fallback: it is what the API did before it was time-zone
 * aware, so the figures stay correct and are simply labelled in UTC.
 */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/**
 * "2026-03-10 02:22:45 PM" in local time.
 *
 * 12-hour with seconds, zero-padded. The leading zero is deliberate: it keeps
 * every timestamp exactly the same width, so a column of them does not shuffle
 * horizontally as the values change. The cells also use `tnum`, which fixes
 * the digit widths for the same reason.
 */
export function formatDateTime(iso: string | null | undefined): string {
  const d = parse(iso)
  if (!d) return '—'
  const hours24 = d.getHours()
  // Midnight and noon are hours 0 and 12 on a 24-hour clock but must read as
  // 12, not 00 — the one case a plain `% 12` gets wrong.
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12
  const meridiem = hours24 < 12 ? 'AM' : 'PM'
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(hours12)}:${pad(d.getMinutes())}:${pad(d.getSeconds())} ${meridiem}`
  )
}

/** "2026-03-10" in local time. */
export function formatDate(iso: string | null | undefined): string {
  const d = parse(iso)
  if (!d) return '—'
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** "3m ago" / "in 2h", for last-used columns. */
export function formatRelative(iso: string | null | undefined): string {
  const d = parse(iso)
  if (!d) return 'never'
  const seconds = Math.round((Date.now() - d.getTime()) / 1000)
  if (seconds < 5) return 'just now'
  const table: Array<[number, string]> = [
    [60, 's'],
    [3600, 'm'],
    [86400, 'h'],
    [2592000, 'd'],
  ]
  if (seconds < 60) return `${seconds}s ago`
  for (let i = 1; i < table.length; i++) {
    const [limit, unit] = table[i]
    const prev = table[i - 1][0]
    if (seconds < limit) return `${Math.floor(seconds / prev)}${unit} ago`
  }
  return `${Math.floor(seconds / 2592000)}mo ago`
}

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

/** First 8 characters of an id, for dense tables. */
export function shortId(id: string | null | undefined): string {
  if (!id) return '—'
  return id.length > 8 ? id.slice(0, 8) : id
}

/**
 * `<input type="date">` value for a Date, in LOCAL time.
 *
 * A date input has no zone: it shows the calendar day the browser is in. That is
 * exactly what these helpers are for, and the reason the API takes a `tz` — the
 * date the user picked means a day on their clock, not a UTC one.
 */
export function toDateInputValue(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function addDays(d: Date, days: number): Date {
  const copy = new Date(d)
  copy.setDate(copy.getDate() + days)
  return copy
}

export function todayInputValue(): string {
  return toDateInputValue(new Date())
}

/** Today minus n-1 days, so a 7-day window includes today. */
export function daysAgoInputValue(days: number): string {
  return toDateInputValue(addDays(new Date(), -(days - 1)))
}

/** Human label for a usage status. */
export function statusLabel(status: string): string {
  switch (status) {
    case 'success':
      return 'Success'
    case 'error':
      return 'Error'
    case 'rejected':
      return 'Rejected'
    case 'canceled':
      return 'Canceled'
    case 'active':
      return 'Active'
    case 'invited':
      return 'Invited'
    case 'suspended':
      return 'Suspended'
    case 'removed':
      return 'Removed'
    case 'revoked':
      return 'Revoked'
    default:
      return status
  }
}

/**
 * Human label for a usage row's operation.
 *
 * The Calls table's Type column is driven by this rather than by the model,
 * because a web call has no model at all — the operation is what identifies the
 * kind of call. Compact forms for the Analytics breakdown live separately, since
 * a chip reading "chat" is right where a column reading "Chat Completion" is not.
 */
export function operationLabel(operation: string): string {
  switch (operation) {
    case 'chat.completion':
      return 'Chat Completion'
    case 'search':
      return 'Web Search'
    case 'fetch':
      return 'Web Fetch'
    case 'image.generate':
      return 'Image Generation'
    case 'embedding':
      return 'Embedding'
    case 'audio.transcription':
      return 'Transcription'
    default:
      return operation
  }
}

/** Human label for a space role. */
export function roleLabel(role: string): string {
  switch (role) {
    case 'owner':
      return 'Owner'
    case 'admin':
      return 'Admin'
    case 'manager':
      return 'Manager'
    case 'member':
      return 'Member'
    default:
      return role
  }
}

/** daisyUI badge modifier for a space role. */
export function roleTone(role: string): string {
  switch (role) {
    case 'owner':
      return 'badge-primary'
    case 'admin':
      return 'badge-info'
    case 'manager':
      return 'badge-secondary'
    default:
      return 'badge-neutral'
  }
}

/**
 * Font Awesome icon for a space role, shown beside a name. A plain member has
 * no icon: it is the default and marking it adds noise.
 */
export function roleIcon(role: string): string {
  switch (role) {
    case 'owner':
      return 'fa-solid fa-crown'
    case 'admin':
      return 'fa-solid fa-user-shield'
    case 'manager':
      return 'fa-solid fa-user-tie'
    default:
      return 'fa-solid fa-user'
  }
}

/** daisyUI badge modifier for a status value. */
export function statusTone(status: string): string {
  switch (status) {
    case 'success':
    case 'active':
      return 'badge-success'
    case 'error':
      return 'badge-error'
    case 'rejected':
      return 'badge-warning'
    case 'invited':
      return 'badge-info'
    case 'canceled':
    case 'removed':
    case 'revoked':
    case 'suspended':
      return 'badge-ghost'
    default:
      return 'badge-ghost'
  }
}
